import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import { User, UserRole } from '../types/user';

interface MyAccountProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onLogout?: () => void;
}

const MyAccount: React.FC<MyAccountProps> = ({ open, onClose, user, onLogout }) => {
  if (!user) return null;

  // Helper function to get role display name
  const getRoleName = (role: UserRole): string => {
    switch (role) {
      case UserRole.CQI_MANAGER:
        return 'CQI Manager (Admin)';
      case UserRole.NATIONAL_USER:
        return 'National User';
      case UserRole.COUNTY_USER:
        return 'County User';
      case UserRole.SUBCOUNTY_USER:
        return 'Subcounty User';
      case UserRole.FACILITY_USER:
        return 'WIT/CHU User';
      default:
        return 'User';
    }
  };

  // Helper function to get jurisdiction display
  const getJurisdictionDisplay = (): React.ReactNode => {
    switch (user.role) {
      case UserRole.CQI_MANAGER:
      case UserRole.NATIONAL_USER:
        return 'National Level (All Counties)';
      case UserRole.COUNTY_USER:
        return user.county || 'Not Assigned';
      case UserRole.SUBCOUNTY_USER:
        return <>
            {user.county && <div>County: {user.county}</div>}
            {user.subCounty && <div>Sub-County: {user.subCounty}</div>}
          </>
      case UserRole.FACILITY_USER:
        return (
          <>
            {user.county && <div>County: {user.county}</div>}
            {user.subCounty && <div>Sub-County: {user.subCounty}</div>}
            {user.facility && <div>WIT/CHU: {user.facility}</div>}
          </>
        );
      default:
        return 'Not Assigned';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="div">
          My Account
        </Typography>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                    Name
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Email
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Role
                  </TableCell>
                  <TableCell>{getRoleName(user.role)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Area of Jurisdiction
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="body1">{getJurisdictionDisplay()}</Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Status
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="body1">
              {user.passwordChangeRequired 
                ? 'Password change required on next login' 
                : 'Account active'}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
        {onLogout && (
          <Button 
            onClick={() => {
              onClose();
              onLogout();
            }} 
            variant="outlined" 
            color="error"
            startIcon={<LogoutIcon />}
          >
            Sign Out
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MyAccount;
